import useSocketReconnect from '../../utils/useSocketReconnect';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import './MerxandatgeOrders.css';
import { can } from '../../utils/permissions';
import { formatMoney } from '../../utils/money';
import { countOrdersByItem, filterOrders, getOrderItemOptions, variantLabel } from './merxandatgeUtils';
import FilterPicker from './FilterPicker';

function MerxandatgeOrders({ socket, userInfo, castellersInfo }) { // Removed unused ...props
    useSocketReconnect(socket, () => {
        if (!isAdmin) return;
        socket.emit('.get_all_merxandatge_orders');
        socket.emit('.get_orders_sheet', response => {
            if (response?.success) {
                setSheetsUrl(response.url || null);
                setSharedWith(response.sharedWith || []);
            }
        });
        if (selectedOrder && isReceiptLoading) socket.emit('.get_merxandatge_receipt', selectedOrder.id);
    });

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [receiptImageUrl, setReceiptImageUrl] = useState(''); // This holds the base64 data URL
  const [receiptImageContent, setReceiptImageContent] = useState(''); // Holds the actual base64 content
  // Removed unused state: directReceiptUrl
  const [isReceiptLoading, setIsReceiptLoading] = useState(false); // Track receipt loading status
  const [itemImages, setItemImages] = useState({}); // Track item images by ID
  const [isExporting, setIsExporting] = useState(false); // Track Google Sheets export status
  const [sheetsUrl, setSheetsUrl] = useState(null); // The colla's permanent orders sheet
  const [sharedWith, setSharedWith] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [isSharing, setIsSharing] = useState(false);
  const [statusFilter, setStatusFilter] = useState(''); // '' = totes
  const [itemFilter, setItemFilter] = useState('');
  const [variantFilter, setVariantFilter] = useState('');
  const [search, setSearch] = useState('');

  const isAdmin = can(userInfo, 'MANAGE_MERCHANDISE');

  // Each image is asked for once: asking again on every orders reply looped
  // (each image that arrived re-ran the effect, which reloaded all orders)
  const requestedImages = useRef(new Set());
  const requestItemImage = itemId => {
    if (!itemId || requestedImages.current.has(itemId)) return;
    requestedImages.current.add(itemId);
    socket.emit('.get_merxandatge_image', itemId);
  };
  // Read by the receipt listener, so opening an order doesn't re-register every listener
  const selectedOrderRef = useRef(null);
  useEffect(() => { selectedOrderRef.current = selectedOrder; }, [selectedOrder]);

  // Status options remain the same
  const statusOptions = [
    { value: 'pending', label: 'Pendent', color: '#FFC107' },
    { value: 'processing', label: 'Pagat', color: '#28A745' },
    { value: 'completed', label: 'Entregat', color: '#17A2B8' },
    { value: 'cancelled', label: 'Cancel·lat', color: '#DC3545' }
  ];

  // The sheet link is permanent, so show it without waiting for an export.
  useEffect(() => {
    if (!isAdmin) return;
    socket.emit('.get_orders_sheet', (response) => {
      if (response?.success) {
        setSheetsUrl(response.url || null);
        setSharedWith(response.sharedWith || []);
      }
    });
  }, [isAdmin, socket]);

  // Effect for fetching data and handling socket events
  useEffect(() => {
    if (!isAdmin) return;

    // Fetch initial data
    socket.emit('.get_all_merxandatge_orders');

    // Define event handlers
    const handleAllOrders = (data) => {
      setOrders(data);
      setLoading(false);
      
      // Request images for each item in each order
      if (data && data.length > 0) {
        data.forEach(order => {
          if (order.items && order.items.length > 0) {
            order.items.forEach(item => requestItemImage(item.itemId));
          }
        });
      }
    };
    const handleOrderUpdated = ({ orderId, status }) => {
      // Re-fetch orders when one is updated
      socket.emit('.get_all_merxandatge_orders');

      // If the order is cancelled, alert the user
      if (status === 'cancelled') {
        alert(`Comanda cancel·lada. S'ha recuperat l'estoc demanat a la botiga.`);
      }
    };
    const handleReceiptUrl = (data) => {
      // This listener handles the response from viewReceipt's emit
      console.log('Received receipt data:', data);
      const selectedOrder = selectedOrderRef.current;
      
      // Only update if the modal is currently open for this order
      if (selectedOrder && data.orderId === selectedOrder.id && data.url) {
         // The URL is expected to be the base64 data URL
        setReceiptImageUrl(data.url);
        
        // Fetch the content from the base64 URL
        fetch(data.url)
          .then(response => response.text())
          .then(content => {
            setReceiptImageContent(content);
            setIsReceiptLoading(false);
          })
          .catch(error => {
            console.error('Error fetching receipt image content:', error);
            alert('No s\'ha pogut carregar el contingut del comprovant.');
            setIsReceiptLoading(false);
          });
      } else if (selectedOrder && data.orderId === selectedOrder.id) {
        console.error('Receipt URL missing in response for order:', data.orderId);
        alert('No s\'ha pogut carregar el comprovant.');
        // Optionally close the modal or clear the image URL if the fetch fails
        // setReceiptImageUrl('');
        setReceiptImageContent('');
      }
      // If the response is for a different order (e.g., due to race conditions), ignore it.
    };
    
    // Handle item image URL responses
    const handleItemImageUrl = (data) => {
      if (data.itemId && data.url) {
        setItemImages(prev => ({
          ...prev,
          [data.itemId]: data.url
        }));
      }
    };

    // Register event listeners
    socket.on('.all_merxandatge_orders', handleAllOrders);
    socket.on('.order_updated', handleOrderUpdated);
    socket.on('.merxandatge_receipt_url', handleReceiptUrl); // Centralized listener
    socket.on('.merxandatge_image_url', handleItemImageUrl); // For item images

    // Cleanup function
    return () => {
      socket.off('.all_merxandatge_orders', handleAllOrders);
      socket.off('.order_updated', handleOrderUpdated);
      socket.off('.merxandatge_receipt_url', handleReceiptUrl);
      socket.off('.merxandatge_image_url', handleItemImageUrl);
    };
    // Depend on isAdmin and socket instance
  }, [isAdmin, socket]);

  // Format date utility
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ca-ES', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Function to open details modal and request receipt if applicable
  const openOrderDetails = (order) => {
    selectedOrderRef.current = order;
    setSelectedOrder(order);
    setReceiptImageUrl(''); // Clear previous receipt immediately
    setReceiptImageContent(''); // Clear previous content
    setIsReceiptLoading(true); // Set loading state to true
    console.log('Requesting receipt for order ID:', order.id);
    socket.emit('.get_merxandatge_receipt', order.id);
    
    // Request images for items in this order if we don't have them yet
    if (order.items && order.items.length > 0) {
      order.items.forEach(item => requestItemImage(item.itemId));
    }
  };

  // Get image URL for an item
  const getItemImageUrl = (itemId) => {
    return itemImages[itemId] || '/default-merchandise.png';
  };

   // Function to handle status change directly from select elements
   const handleStatusChange = (orderId, newStatus) => {
    // Get the current order
    const order = orders.find(o => o.id === orderId);
    if (!order) return;
    
    // Don't allow changing status of cancelled orders
    if (order.status === 'cancelled') {
      alert("Les comandes cancel·lades no es poden modificar. Aquesta acció és irreversible.");
      return;
    }
    
    // If changing to cancelled, ask for confirmation
    if (newStatus === 'cancelled') {
      const confirmed = window.confirm(
        "Estàs segur que vols cancel·lar aquesta comanda?\n\n" +
        "Aquest canvi és IRREVERSIBLE i l'estoc dels productes serà recuperat automàticament.\n\n" +
        "Prem OK per confirmar la cancel·lació."
      );
      if (!confirmed) return;
    }
    
    socket.emit('.update_merxandatge_order_status', { orderId, status: newStatus });
    // Optimistic UI update (optional, handled by '.order_updated' event anyway)
    // setOrders(prevOrders => prevOrders.map(o => o.id === orderId ? { ...o, status: newStatus } : o));
    if (selectedOrder && selectedOrder.id === orderId) {
        setSelectedOrder(prev => ({ ...prev, status: newStatus }));
    }
  };

  // Get style object for status display
  const getStatusStyle = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status) || statusOptions[0]; // Default to pending style if unknown
    return {
      backgroundColor: statusOption.color,
      color: 'white',
      padding: '4px 8px',
      borderRadius: '4px',
      fontWeight: 'bold',
      display: 'inline-block', // Ensure span behaves like a block for padding
      minWidth: '80px', // Give status labels consistent width
      textAlign: 'center', // Center text
    };
  };

  // Close the details modal
  const closeOrderDetails = () => {
    setSelectedOrder(null);
    setReceiptImageUrl(''); // Clear the receipt URL when closing
    setReceiptImageContent(''); // Clear the receipt content when closing
    setIsReceiptLoading(false); // Reset loading state
    // Removed reset for directReceiptUrl
  };

  // Build casteller display from castellersInfo
  const getCastellerDisplay = (userId, fallbackName, fallbackId) => {
    const info = castellersInfo?.[userId];
    if (info) {
      const nom = info?.nom || '';
      const primerCognom = (info && (info['primer-cognom'] ?? info.cognom)) || '';
      const segonCognom = info?.['segon-cognom'] || '';
      const mote = info?.mote || '';
      const base = [nom, primerCognom, segonCognom].filter(Boolean).join(' ');
      return mote ? `${base} (${mote})` : base || fallbackName || (fallbackId ? `ID: ${fallbackId}` : '');
    }
    return fallbackName || (fallbackId ? `ID: ${fallbackId}` : '');
  };

  // Get casteller fields separately for table/export
  const getCastellerFields = (userId) => {
    const info = castellersInfo?.[userId];
    const nom = info?.nom || '';
    const cognom = (info && (info['primer-cognom'] ?? info.cognom)) || '';
    const segonCognom = info?.['segon-cognom'] || '';
    const mote = info?.mote || '';
    return { nom, cognom, segonCognom, mote };
  };


  // Mode TPV de l'app: sales recorded by the junta, already collected.
  const POS_PAYMENT_LABELS = { cash: 'Efectiu', card_terminal: 'Datàfon' };
  const getPosSale = (order) => {
    const method = POS_PAYMENT_LABELS[order.payment_method];
    if (!method) return null;
    const seller = order.sold_by ? getCastellerDisplay(order.sold_by, '', order.sold_by) : '';
    return { method, seller };
  };

  const buildVariantDetails = (variant) => {
    if (!variant) return [];

    const details = [];
    if (variant.size) details.push({ label: 'Talla', value: variant.size });
    if (variant.color) details.push({ label: 'Color', value: variant.color });

    Object.keys(variant)
      .filter(key => !['size', 'color', 'id'].includes(key))
      .forEach(key => {
        const value = variant[key];
        if (value === undefined || value === null || value === '') return;
        const formattedLabel = key
          .split('_')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        details.push({ label: formattedLabel, value });
      });

    return details;
  };

  const openLink = (url) => {
    // An anchor click is more reliable than window.open against pop-up blockers.
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // The sheet is kept up to date automatically; this forces an immediate refresh.
  const openOrdersSheet = () => {
    if (sheetsUrl) openLink(sheetsUrl);
    setIsExporting(true);
    socket.emit('.export_orders_to_sheets', {}, (response) => {
      setIsExporting(false);
      if (response.success && response.url) {
        if (!sheetsUrl) openLink(response.url);
        setSheetsUrl(response.url);
      } else {
        alert(response.message || 'Error actualitzant el Google Sheet');
      }
    });
  };

  const shareOrdersSheet = (event) => {
    event.preventDefault();
    if (!shareEmail.trim()) return;
    setIsSharing(true);
    socket.emit('.share_orders_sheet', shareEmail.trim(), (response) => {
      setIsSharing(false);
      if (response.success) {
        setSharedWith(response.sharedWith || []);
        setSheetsUrl(response.url || sheetsUrl);
        setShareEmail('');
      } else {
        alert(response.message || 'No s\'ha pogut compartir el Google Sheet');
      }
    });
  };

  const itemOptions = useMemo(() => getOrderItemOptions(orders), [orders]);
  const selectedItem = itemOptions.find(option => option.key === itemFilter);
  const getOrderName = (order) => {
    if (order.customer_name) return order.customer_name;
    const { nom, cognom, segonCognom, mote } = getCastellerFields(order.user_id);
    return [nom, cognom, segonCognom, mote, order.user_name].join(' ');
  };
  const itemFilters = { itemKey: itemFilter, variantKey: variantFilter };
  // Status counts follow the other filters, so each tab says what clicking it shows.
  const ordersBeforeStatus = filterOrders(orders, { ...itemFilters, search }, getOrderName);
  const filteredOrders = filterOrders(ordersBeforeStatus, { statuses: statusFilter ? [statusFilter] : [] });
  // Picker counts follow status and search, like the tabs follow the other filters.
  const ordersBeforeItem = filterOrders(orders, { statuses: statusFilter ? [statusFilter] : [], search }, getOrderName);
  const productCounts = countOrdersByItem(ordersBeforeItem);
  const variantCounts = itemFilter ? countOrdersByItem(ordersBeforeItem, itemFilter) : new Map();
  // Status lives in the tabs; these are the refinements shown as removable chips.
  const activeFilters = [
    ...(selectedItem ? [{ key: 'item', label: selectedItem.name, clear: () => { setItemFilter(''); setVariantFilter(''); } }] : []),
    ...(variantFilter ? [{ key: 'variant', label: variantLabel(variantFilter), clear: () => setVariantFilter('') }] : []),
    ...(search.trim() ? [{ key: 'search', label: `“${search.trim()}”`, clear: () => setSearch('') }] : []),
  ];

  const clearFilters = () => {
    setStatusFilter('');
    setItemFilter('');
    setVariantFilter('');
    setSearch('');
  };

  // Render Access Denied if not Admin
  if (!isAdmin) {
    return (
      <div className="merxandatge-orders-container">
        <h2>Accés denegat</h2>
        <p>Cal ser membre de la junta per gestionar les comandes.</p>
      </div>
    );
  }

  // Main Render for Admin
  return (
    <div className="merxandatge-orders-container">

      {/* Orders List Section */}
      <div className="orders-header">
        <h2 style={{ margin: 0 }}>Llistat de Comandes</h2>
        <div className="orders-sheet-actions">
          <button
            onClick={openOrdersSheet}
            disabled={isExporting && !sheetsUrl}
            className="orders-sheet-button"
            title="Un únic document que s'actualitza sol amb cada comanda"
          >
            {isExporting ? (
              <span className="spinner-small" style={{ width: 16, height: 16, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
            ) : (
              <i className="fas fa-table" />
            )}
            {isExporting ? 'Actualitzant...' : 'Obrir Google Sheet'}
          </button>
          <button
            onClick={() => setShowShare(previous => !previous)}
            className="orders-share-toggle"
            aria-expanded={showShare}
          >
            <i className="fas fa-user-plus" /> Compartir
          </button>
        </div>
      </div>

      {showShare ? (
        <div className="orders-share-panel">
          <p className="orders-share-help">
            El Google Sheet és sempre el mateix i s'actualitza sol amb cada comanda o canvi d'estoc. Les pestanyes
            <strong> Articles</strong>, <strong>Comandes</strong> i <strong>Estoc</strong> tenen les dades en brut
            (l'estoc es modifica des d'Aleta); podeu afegir-hi pestanyes pròpies amb fórmules, taules dinàmiques i gràfics.
          </p>
          <form onSubmit={shareOrdersSheet} className="orders-share-form">
            <input
              type="email"
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              placeholder="correu@gmail.com"
              aria-label="Correu per compartir com a editor"
            />
            <button type="submit" disabled={isSharing || !shareEmail.trim()}>
              {isSharing ? 'Compartint...' : 'Afegir editor'}
            </button>
          </form>
          {sharedWith.length > 0 ? (
            <div className="orders-share-list">
              {sharedWith.map(email => <span key={email} className="item-pill">{email}</span>)}
            </div>
          ) : null}
        </div>
      ) : null}

      {orders.length > 0 ? (
        <div className="orders-filters">
          <div className="orders-status-tabs" role="tablist" aria-label="Estat de la comanda">
            {[{ value: '', label: 'Totes' }, ...statusOptions].map(option => {
              const active = statusFilter === option.value;
              const count = option.value
                ? ordersBeforeStatus.filter(order => order.status === option.value).length
                : ordersBeforeStatus.length;
              return (
                <button
                  key={option.value || 'all'}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  className={`orders-status-tab${active ? ' active' : ''}`}
                  onClick={() => setStatusFilter(option.value)}
                >
                  {option.label}
                  <span className="orders-status-count">{count}</span>
                </button>
              );
            })}
          </div>

          <div className="orders-toolbar">
            <div className="orders-search">
              <i className="fas fa-search" aria-hidden="true" />
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca per nom o sobrenom"
                aria-label="Cerca per nom"
              />
            </div>
            <FilterPicker
              label="Filtrar per article"
              icon="fa-tshirt"
              allLabel="Tots els articles"
              searchPlaceholder="Cerca un article"
              options={itemOptions.map(option => ({ value: option.key, label: option.name, count: productCounts.get(option.key) || 0 }))}
              value={itemFilter}
              onChange={(key) => { setItemFilter(key); setVariantFilter(''); }}
            />
            {selectedItem && selectedItem.variants.length > 1 ? (
              <FilterPicker
                label="Filtrar per talla o color"
                icon="fa-ruler"
                allLabel="Totes les talles"
                searchPlaceholder="Cerca una talla"
                options={selectedItem.variants.map(key => ({ value: key, label: variantLabel(key), count: variantCounts.get(key) || 0 }))}
                value={variantFilter}
                onChange={setVariantFilter}
              />
            ) : null}
          </div>

          {activeFilters.length > 0 ? (
            <div className="orders-active-filters">
              {activeFilters.map(filter => (
                <span key={filter.key} className="orders-filter-chip">
                  {filter.label}
                  <button type="button" onClick={filter.clear} aria-label={`Treure el filtre ${filter.label}`}>×</button>
                </span>
              ))}
              <button type="button" className="orders-clear-filters" onClick={clearFilters}>Esborra els filtres</button>
            </div>
          ) : null}
        </div>
      ) : null}

      {loading ? (
        <div className="orders-list">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="order-card order-card-skeleton">
              <div className="order-card-row">
                <div className="order-card-content">
                  <div className="skeleton-line skeleton-date" />
                  <div className="order-details-row">
                    <div className="order-customer">
                      <span className="section-label">Casteller</span>
                      <div className="skeleton-line skeleton-name" />
                    </div>
                    <div className="order-items">
                      <span className="section-label">Articles</span>
                      <div className="items-badges">
                        <div className="skeleton-pill" />
                        <div className="skeleton-pill skeleton-pill-short" />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="order-actions">
                  <div className="order-total">
                    <span className="section-label">Import</span>
                    <div className="skeleton-line skeleton-price" />
                  </div>
                  <div className="order-status">
                    <div className="skeleton-select" />
                  </div>
                  <div className="skeleton-button" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div className="no-orders">No hi ha comandes disponibles</div>
      ) : filteredOrders.length === 0 ? (
        <div className="no-orders orders-empty-filtered">
          <p>Cap comanda coincideix amb aquests filtres.</p>
          <button type="button" className="orders-clear-filters" onClick={clearFilters}>Esborra els filtres</button>
        </div>
      ) : (
        <div className="orders-list">
          {filteredOrders.map(order => {
            const { nom, cognom, segonCognom, mote } = getCastellerFields(order.user_id);
            const castellerDisplay = order.customer_name
              ? `${order.customer_name} (extern)`
              : getCastellerDisplay(order.user_id, `${nom} ${cognom}`.trim(), order.user_id);
            const posSale = getPosSale(order);
            const itemBadges = (order.items || []).map((item, idx) => {
              const details = [];
              if (item?.variant?.size) details.push(item.variant.size);
              if (item?.variant?.color) details.push(item.variant.color);
              const detailText = details.length ? ` (${details.join(', ')})` : '';
              const quantity = typeof item.quantity === 'number' ? item.quantity : 0;
              return (
                <span key={`${order.id}-item-${idx}`} className="item-pill">
                  {item?.name || 'Article'}{detailText} ×{quantity}
                </span>
              );
            });

            return (
              <div key={order.id} className={`order-card status-${order.status}`}>
                <div className="order-card-row">
                  <div className="order-card-content">
                    <div className="order-date">{formatDate(order.created_at)}</div>

                    <div className="order-details-row">
                      <div className="order-customer">
                        <span className="section-label">Casteller</span>
                        <div className="section-value">{castellerDisplay || `${nom} ${cognom} ${segonCognom} ${mote}`.trim()}</div>
                        {posSale ? (
                          <div className="order-pos-sale">
                            <span className="order-pos-badge">Venda de la junta · {posSale.method}</span>
                            {posSale.seller ? <span className="muted">Venut per {posSale.seller}</span> : null}
                          </div>
                        ) : null}
                      </div>

                      <div className="order-items">
                        <span className="section-label">Articles</span>
                        <div className="items-badges">
                          {itemBadges.length > 0 ? itemBadges : <span className="muted">Sense articles</span>}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="order-actions">
                    <div className="order-total">
                      <span className="section-label">Import</span>
                      <span className="section-value">{order.total != null ? formatMoney(order.total) : 'N/A'}€</span>
                    </div>
                    <div className="order-status">
                      <select
                        value={order.status}
                        onChange={(e) => handleStatusChange(order.id, e.target.value)}
                        style={{ backgroundColor: getStatusStyle(order.status).backgroundColor, color: 'white' }}
                        className="status-select compact"
                        disabled={order.status === 'cancelled'}
                      >
                        {statusOptions.map(option => (
                          <option key={option.value} value={option.value} style={{ backgroundColor: 'white', color: 'black'}}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      className="details-button"
                      onClick={() => openOrderDetails(order)}
                    >
                      <i className="fas fa-file-invoice"></i> Comprovant
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Order Details Modal */}
      {selectedOrder && (
        <div className="modal-backdrop" onClick={closeOrderDetails}>
          <div className="order-details-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Detalls de la Comanda ({selectedOrder.id.substring(0,8)}...)</h2>
              <button className="close-button" onClick={closeOrderDetails}>×</button>
            </div>

            <div className="modal-body">
              {/* Receipt Section - Simplified */}
              {receiptImageContent ? ( // Show only if receiptImageContent has content
                <div className="receipt-section">
                  <h3>Comprovant de Pagament</h3>
                  <div className="receipt-image-container">
                    {/* Use receiptImageContent directly for src */}
                    <img
                      src={receiptImageContent}
                      alt="Comprovant de pagament"
                      className="receipt-image"
                      onError={(e) => {
                        console.error('Error loading base64 receipt image.');
                        e.target.alt = 'Error al carregar el comprovant'; // Provide feedback
                        // Optionally hide the broken image or show a placeholder
                        // e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
              ) : (
                <div className="receipt-section">
                  <h3>Comprovant de Pagament</h3>
                  {isReceiptLoading ? (
                    <p>Carregant comprovant...</p>
                  ) : (
                    <p>No hi ha cap comprovant disponible per aquesta comanda.</p>
                  )}
                </div>
              )}

              {/* Order Info */}
              <div className="order-info">
                 {/* Use grid or flexbox for better alignment if needed */}
                <p><strong>ID:</strong> {selectedOrder.id}</p>
                <p><strong>Data:</strong> {formatDate(selectedOrder.created_at)}</p>
                {selectedOrder.customer_name ? (
                  <p><strong>Client extern:</strong> {selectedOrder.customer_name}</p>
                ) : (() => { const f = getCastellerFields(selectedOrder.user_id); return (
                  <p><strong>Casteller:</strong> {[f.nom, f.cognom, f.segonCognom].filter(Boolean).join(' ')}{f.mote ? ` (${f.mote})` : ''}</p>
                )})()}
                {(() => { const posSale = getPosSale(selectedOrder); return posSale ? (
                  <p><strong>Venda de la junta:</strong> {posSale.method}{posSale.seller ? `, registrada per ${posSale.seller}` : ''}</p>
                ) : null; })()}
                <p><strong>Import:</strong> {selectedOrder.total != null ? formatMoney(selectedOrder.total) : 'N/A'}€</p>
                <p><strong>Estat:</strong> <span style={getStatusStyle(selectedOrder.status)}>
                  {statusOptions.find(opt => opt.value === selectedOrder.status)?.label || selectedOrder.status}
                </span></p>
              </div>

              {/* Items Table */}
              <h3>Articles</h3>
              <table className="items-table">
                <thead>
                  <tr>
                    <th>Imatge</th>
                    <th>Article</th>
                    <th>Detalls Variant</th>
                    <th>Quant.</th>
                    <th>Preu Uni.</th>
                    <th>Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedOrder.items?.map((item, index) => (
                    <tr key={index}>
                      <td className="item-image-cell">
                        <img 
                          src={getItemImageUrl(item.itemId)}
                          alt={item.name}
                          className="item-thumbnail"
                          onError={(e) => {
                            e.target.src = '/default-merchandise.png';
                          }}
                        />
                      </td>
                      <td>{item.name}{item.kind === 'pack' && <ul>{(item.components || []).map(component => <li key={component.itemId}>{component.quantity * item.quantity} × {component.name}{[component.variant?.size, component.variant?.color].filter(Boolean).length ? ` · ${[component.variant?.size, component.variant?.color].filter(Boolean).join(' · ')}` : ''}{component.surcharge > 0 ? ` (+${formatMoney(component.surcharge)}€ / u.)` : ''}</li>)}</ul>}</td>
                      <td className="variant-cell">
                        {(() => {
                          const details = buildVariantDetails(item.variant);
                          if (details.length === 0) {
                            return <span className="variant-pill variant-pill-empty">Sense variants</span>;
                          }
                          return details.map((detail, detailIndex) => (
                            <span key={`${detail.label}-${detailIndex}`} className="variant-pill">
                              <strong>{detail.label}:</strong> {detail.value}
                            </span>
                          ));
                        })()}
                      </td>
                      <td>{item.quantity}</td>
                      <td>{item.price != null ? formatMoney(item.price) : 'N/A'}€</td>
                      <td>{formatMoney(item.price * item.quantity)}€</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan="5" className="total-label">Total</td>
                    <td className="total-value">{selectedOrder.total != null ? formatMoney(selectedOrder.total) : 'N/A'}€</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Modal Footer */}
            <div className="modal-footer">
               {/* Status changer inside the modal */}
               <label htmlFor="modalStatusSelect">Canviar Estat:</label>
               <div style={{ position: 'relative', display: 'inline-block' }}>
                 <select
                      id="modalStatusSelect"
                      value={selectedOrder.status}
                      onChange={(e) => handleStatusChange(selectedOrder.id, e.target.value)}
                      className="status-select-large" // Keep class for specific styling
                      style={{ backgroundColor: getStatusStyle(selectedOrder.status).backgroundColor, color: 'white' }}
                      disabled={selectedOrder.status === 'cancelled'} // Disable if already cancelled
                  >
                      {statusOptions.map(option => (
                          <option key={option.value} value={option.value} style={{ backgroundColor: 'white', color: 'black'}}>
                              {option.label}
                          </option>
                      ))}
                  </select>
                  <i className="fas fa-chevron-down" style={{ 
                    position: 'absolute', 
                    right: '12px', 
                    top: '50%', 
                    transform: 'translateY(-50%)', 
                    pointerEvents: 'none',
                    color: 'white',
                    fontSize: '12px'
                  }}></i>
               </div>
              <button className="close-modal-button" onClick={closeOrderDetails}>Tancar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MerxandatgeOrders;